export type PricingServiceId = "aro" | "aeo";
export type PricingChannelId = "ai_agents" | "live_chat" | "phone_sms" | "email";

export type PricingSelection = {
  services: PricingServiceId[];
  channels: PricingChannelId[];
};

export const ARO_MONTHLY_CENTS = 4900;
export const AEO_MONTHLY_CENTS = 4900;
export const CHANNEL_MONTHLY_CENTS: Record<PricingChannelId, number> = {
  ai_agents: 2900,
  live_chat: 2900,
  phone_sms: 9900,
  email: 2900,
};

export const PRICING_SERVICES: {
  id: PricingServiceId;
  name: string;
  headline: string;
  monthlyCents: number;
  features: string[];
  planned?: boolean;
}[] = [
  {
    id: "aro",
    name: "ARO — Agent Readiness Optimization",
    headline: "Get Discovered and Connected.",
    monthlyCents: ARO_MONTHLY_CENTS,
    features: [
      "ARO audit and benchmark reporting",
      "Prioritized recommendations",
      "WordPress plugin for supported ARO artifacts",
      "Rescan and verification workflow",
      "Does not include the SiteGlue Agent AI Agents channel",
    ],
  },
  {
    id: "aeo",
    name: "AEO — Answer Engine Optimization",
    headline: "Get Recommended.",
    monthlyCents: AEO_MONTHLY_CENTS,
    features: [
      "AI visibility auditing (live tests when available)",
      "Brand mention and citation analysis (planned until live tests run)",
      "Competitive comparisons (planned)",
      "Answer accuracy (planned)",
      "Optimization recommendations",
    ],
    planned: true,
  },
];

export const PRICING_CHANNELS: {
  id: PricingChannelId;
  name: string;
  monthlyCents: number;
  help: string;
}[] = [
  {
    id: "ai_agents",
    name: "AI Agents",
    monthlyCents: CHANNEL_MONTHLY_CENTS.ai_agents,
    help: "SiteGlue MCP server and supported agent-to-agent actions: book, callback, approved information, team connect, and authorized integrations.",
  },
  {
    id: "live_chat",
    name: "Live Chat",
    monthlyCents: CHANNEL_MONTHLY_CENTS.live_chat,
    help: "Website chat for visitors, with Team Handoff™.",
  },
  {
    id: "phone_sms",
    name: "Phone Calls + Text Messages",
    monthlyCents: CHANNEL_MONTHLY_CENTS.phone_sms,
    help: "Phone and SMS as one channel bundle. Not a separate optimization service.",
  },
  {
    id: "email",
    name: "Email",
    monthlyCents: CHANNEL_MONTHLY_CENTS.email,
    help: "Email conversations on the same SiteGlue Agent.",
  },
];

export function emptyPricingSelection(): PricingSelection {
  return { services: [], channels: [] };
}

export function togglePricingService(selection: PricingSelection, id: PricingServiceId): PricingSelection {
  return {
    ...selection,
    services: selection.services.includes(id)
      ? selection.services.filter((item) => item !== id)
      : [...selection.services, id],
  };
}

export function togglePricingChannel(selection: PricingSelection, id: PricingChannelId): PricingSelection {
  return {
    ...selection,
    channels: selection.channels.includes(id)
      ? selection.channels.filter((item) => item !== id)
      : [...selection.channels, id],
  };
}

export function pricingMonthlyTotalCents(selection: PricingSelection): number {
  const serviceTotal = selection.services.reduce(
    (sum, id) => sum + (id === "aro" ? ARO_MONTHLY_CENTS : AEO_MONTHLY_CENTS),
    0,
  );
  const channelTotal = selection.channels.reduce((sum, id) => sum + CHANNEL_MONTHLY_CENTS[id], 0);
  return serviceTotal + channelTotal;
}

export function formatUsdFromCents(cents: number): string {
  return `$${Math.round(cents / 100)}`;
}

export function serializePricingQuery(selection: PricingSelection): {
  services: string;
  channels: string;
  totalCents: string;
} {
  return {
    services: selection.services.join(","),
    channels: selection.channels.join(","),
    totalCents: String(pricingMonthlyTotalCents(selection)),
  };
}

export function parsePricingServices(raw: string | null | undefined): PricingServiceId[] {
  if (!raw?.trim()) return [];
  const allowed = new Set<PricingServiceId>(["aro", "aeo"]);
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is PricingServiceId => allowed.has(part as PricingServiceId));
}

export function parsePricingChannels(raw: string | null | undefined): PricingChannelId[] {
  if (!raw?.trim()) return [];
  const allowed = new Set<PricingChannelId>(["ai_agents", "live_chat", "phone_sms", "email"]);
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is PricingChannelId => allowed.has(part as PricingChannelId));
}

/** Display-only mapping to today's Intern/Associate/Professional checkout. */
export function recommendedCheckoutPlan(selection: PricingSelection): "intern" | "associate" | "professional" {
  if (selection.channels.includes("phone_sms")) return "professional";
  if (selection.channels.includes("email")) return "associate";
  return "intern";
}

export const PRICING_EXAMPLE = {
  services: ["aro"] as PricingServiceId[],
  channels: ["ai_agents", "live_chat"] as PricingChannelId[],
  totalCents: 10700,
};

export function readPricingOnboardContext(searchParams: {
  get(name: string): string | null;
}): {
  services: PricingServiceId[];
  wordpressDetected: boolean;
  proposedTotalCents: number | null;
} | null {
  const services = parsePricingServices(searchParams.get("services"));
  const wordpressDetected = searchParams.get("wp") === "1";
  const rawTotal = searchParams.get("totalCents");
  const parsedTotal = rawTotal ? Number(rawTotal) : NaN;
  const proposedTotalCents = Number.isFinite(parsedTotal) ? parsedTotal : null;
  if (!services.length && !wordpressDetected && proposedTotalCents == null) return null;
  return { services, wordpressDetected, proposedTotalCents };
}
